import React, { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Bill } from "@common/types/bill";
import { Task } from "@common/types/task";
import { BillViewSchema } from "@common/types/views/billViews";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { StickyTaskMembershipPending } from "../shared/StickyTaskMembershipPending";
import { useStickyRefreshOnMount } from "../shared/useStickyRefreshOnMount";
import { useTaskTableViewState } from "../shared/useTaskTableViewState";
import { BillsTaskBillDetails } from "./BillsTaskBillDetails";
import { BillsTaskTable } from "./BillsTaskTable";
import { getBillsTaskFilterTabs } from "./billsTaskFilters";
import { parseBillsTaskView } from "./billsTaskView";
import styles from "./Bills.module.scss";

interface Props {
    selectedTask: Task;
}

/**
 * Task body of a BILLS task: the bills table scoped by the task view. Sticky
 * tasks show their pinned membership and the creator's criteria as locked
 * pills; tasks created from a selection show exactly the selected bills.
 */
export const BillsTaskBody: React.FC<Props> = ({ selectedTask }) => {
    const queryClient = useQueryClient();
    const {
        taskView,
        sizedView,
        allowedColumnIds,
        handleViewChange,
        handleFiltersChange,
    } = useTaskTableViewState({
        selectedTask,
        viewSchema: BillViewSchema,
        parseView: parseBillsTaskView,
    });
    const originalFilters = selectedTask.view?.originalFilters ?? undefined;
    const [selectedBill, setSelectedBill] = useState<Bill>();

    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ["bills"] });
    }, [queryClient]);

    const { isMembershipPending, resolvedMembership } = useStickyRefreshOnMount(
        { selectedTask, onMembershipAdded: invalidateList },
    );
    const stickyBillIds =
        resolvedMembership?.billIds ?? selectedTask.view?.filters?.billIds;

    const handleBillClick = useCallback((bill?: Bill) => {
        setSelectedBill((previous) =>
            bill && previous?.id === bill.id ? undefined : bill,
        );
    }, []);

    const availableFilters = useMemo(
        () =>
            originalFilters
                ? []
                : getBillsTaskFilterTabs({ isWorkspaceScoped: true }),
        [originalFilters],
    );

    const isScopeLocked =
        !!originalFilters || !!taskView.filters.billIds?.length;

    if (isMembershipPending) {
        return <StickyTaskMembershipPending />;
    }

    return (
        <div className={styles.root}>
            <BillsTaskTable
                view={sizedView}
                onViewChange={handleViewChange}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                defaultView={taskView}
                allowedColumnIds={allowedColumnIds}
                lockScope={isScopeLocked}
                originalFilters={originalFilters}
                stickyBillIds={stickyBillIds}
                onBillClick={handleBillClick}
                selectedBillId={selectedBill?.id}
                paginationType="loadMore"
                billDetails={
                    selectedBill && (
                        <BillsTaskBillDetails
                            workspaceId={selectedTask.workspaceId}
                            bill={selectedBill}
                            setBill={setSelectedBill}
                        />
                    )
                }
                className={styles.filters}
                tableClassName={styles.table}
                emptyView={() => (
                    <EmptyState
                        header={
                            originalFilters || taskView.filters.billIds
                                ? "No bills associated with this task"
                                : "No bills matching the task definition"
                        }
                    />
                )}
            />
        </div>
    );
};
